import React, { useState, useEffect } from 'react';
import { getToken } from '../utils/authStorage';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import './Auth.css';

const RequestVerification = () => {
  const [step, setStep] = useState('info'); // 'info', 'submitting', 'success'
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [verificationStatus, setVerificationStatus] = useState(null);
  const navigate = useNavigate();

  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:9090/api';

  useEffect(() => {
    checkVerificationStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkVerificationStatus = async () => {
    try {
      const token = getToken();
      const response = await axios.get(`${API_URL}/verification/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setVerificationStatus(response.data);
    } catch (err) {
      console.error('Failed to fetch verification status:', err);
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    setStep('submitting');

    try {
      const token = getToken();
      await axios.post(`${API_URL}/verification/request-badge`, {}, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      setStep('success');
    } catch (err) {
      console.error('Verification request failed:', err);
      setError(err.response?.data?.error || 'Failed to submit verification request');
      setStep('info');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelRequest = async () => {
    if (!window.confirm('Are you sure you want to cancel your verification request?')) {
      return;
    }

    setLoading(true);
    setError('');

    try {
      const token = getToken();
      await axios.delete(`${API_URL}/verification/cancel`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      // Refresh verification status
      await checkVerificationStatus();
      
      alert('Verification request cancelled successfully');
    } catch (err) {
      console.error('Failed to cancel verification request:', err);
      setError(err.response?.data?.error || err.response?.data?.message || 'Failed to cancel request');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-card" style={{ maxWidth: '500px' }}>
        {step === 'info' && (
          <>
            <h2>Get Verified ✓</h2>
            <p style={{ color: 'var(--text-secondary)', marginBottom: '24px' }}>
              Get a blue verification badge to show you're a real person!
            </p>

            <div style={{
              background: 'var(--surface)',
              padding: '20px',
              borderRadius: '12px',
              marginBottom: '24px',
              border: '1px solid var(--border)'
            }}>
              <h3 style={{ marginBottom: '16px', fontSize: '18px', color: 'var(--text-primary)' }}>How it works:</h3>
              <ol style={{ paddingLeft: '20px', lineHeight: '1.8', color: 'var(--text-primary)' }}>
                <li>Submit your verification request</li>
                <li>Wait for an admin to review your request</li>
                <li>The admin will call you via video to verify your identity</li>
                <li>Answer the call and show a simple hand gesture (face optional)</li>
                <li>Get your verification badge!</li>
              </ol>
            </div>

            <div style={{
              background: 'rgba(34, 197, 94, 0.1)',
              border: '1px solid rgba(34, 197, 94, 0.3)',
              padding: '16px',
              borderRadius: '8px',
              marginBottom: '16px'
            }}>
              <p style={{ margin: 0, fontSize: '14px', color: 'var(--text-primary)' }}>
                <strong style={{ color: '#22c55e' }}>✓ Privacy Protected:</strong><br/>
                You can hide your face if you want! We only need to see a hand gesture.
                The video call is private and only our verification team will see it.
              </p>
            </div>

            <div style={{
              background: 'rgba(59, 130, 246, 0.1)',
              border: '1px solid rgba(59, 130, 246, 0.3)',
              padding: '16px',
              borderRadius: '8px',
              marginBottom: '16px'
            }}>
              <p style={{ margin: 0, fontSize: '14px', color: 'var(--text-primary)' }}>
                <strong style={{ color: '#3b82f6' }}>📹 Video Call Verification:</strong><br/>
                An admin will initiate a video call to you. Make sure to keep your notifications enabled 
                so you don't miss the call! The call is quick (1-2 minutes) and you'll be asked to show 
                a simple hand gesture.
              </p>
            </div>

            {verificationStatus?.hasVerifiedBadge && (
              <div style={{
                background: 'rgba(34, 197, 94, 0.3)',
                border: '1px solid rgba(34, 197, 94, 0.6)',
                padding: '16px',
                borderRadius: '8px',
                marginBottom: '16px',
                textAlign: 'center'
              }}>
                <div style={{ fontSize: '48px', marginBottom: '8px' }}>✅</div>
                <p style={{ margin: 0, fontSize: '16px', fontWeight: 'bold' }}>
                  You're already verified!
                </p>
              </div>
            )}

            {verificationStatus?.requests?.some(r => r.status === 'PENDING') && (
              <div style={{
                background: 'rgba(251, 191, 36, 0.1)',
                border: '1px solid rgba(251, 191, 36, 0.3)',
                padding: '20px',
                borderRadius: '12px',
                marginBottom: '16px',
                textAlign: 'center'
              }}>
                <div style={{ fontSize: '48px', marginBottom: '12px' }}>⏳</div>
                <h3 style={{ 
                  fontSize: '20px', 
                  fontWeight: '700', 
                  color: '#f59e0b',
                  marginBottom: '8px'
                }}>
                  Approval Pending
                </h3>
                <p style={{ margin: 0, fontSize: '14px', color: 'var(--text-primary)', marginBottom: '16px' }}>
                  Your verification request is being reviewed. Our team will contact you soon for a video call.
                </p>
                <button
                  onClick={handleCancelRequest}
                  disabled={loading}
                  style={{
                    background: 'none',
                    border: '1px solid rgba(239, 68, 68, 0.5)',
                    color: '#ef4444',
                    padding: '10px 20px',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '600',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.background = 'rgba(239, 68, 68, 0.1)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = 'none';
                  }}
                >
                  Cancel Request
                </button>
              </div>
            )}

            {error && (
              <div style={{
                padding: '12px',
                backgroundColor: '#fee2e2',
                borderRadius: '8px',
                marginBottom: '16px',
                color: '#991b1b'
              }}>
                {error}
              </div>
            )}

            <button
              onClick={handleSubmit}
              disabled={loading || verificationStatus?.hasVerifiedBadge || 
                       verificationStatus?.requests?.some(r => r.status === 'PENDING')}
              className="btn btn-primary"
              style={{ width: '100%' }}
            >
              {verificationStatus?.hasVerifiedBadge ? 'Already Verified' : 
               verificationStatus?.requests?.some(r => r.status === 'PENDING') ? 'Approval Pending' :
               'Request Verification'}
            </button>

            <button
              onClick={() => navigate('/profile')}
              style={{
                background: 'none',
                border: 'none',
                color: 'var(--text-secondary)',
                cursor: 'pointer',
                marginTop: '12px',
                width: '100%'
              }}
            >
              Cancel
            </button>
          </>
        )}

        {step === 'submitting' && (
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>⏳</div>
            <h2 style={{ color: 'var(--text-primary)' }}>Submitting Request...</h2>
            <p style={{ color: 'var(--text-secondary)' }}>
              Please wait while we process your request
            </p>
          </div>
        )}

        {step === 'success' && (
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            <div style={{ 
              fontSize: '72px', 
              marginBottom: '24px',
              animation: 'fadeIn 0.5s ease-in'
            }}>✅</div>
            
            <h2 style={{ 
              fontSize: '28px', 
              fontWeight: '800', 
              marginBottom: '16px',
              background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text'
            }}>
              Request Submitted Successfully!
            </h2>
            
            <p style={{ 
              color: 'var(--text-secondary)', 
              marginBottom: '32px',
              fontSize: '15px',
              lineHeight: '1.6',
              maxWidth: '400px',
              margin: '0 auto 32px'
            }}>
              Your verification request is now in our queue. Our team will review and contact you shortly.
            </p>

            <div style={{
              background: 'rgba(59, 130, 246, 0.1)',
              border: '2px solid rgba(59, 130, 246, 0.3)',
              padding: '24px',
              borderRadius: '16px',
              marginBottom: '32px',
              textAlign: 'left'
            }}>
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '12px',
                marginBottom: '16px'
              }}>
                <div style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '12px',
                  background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '20px'
                }}>📹</div>
                <h3 style={{ 
                  margin: 0, 
                  fontSize: '18px', 
                  fontWeight: '700',
                  color: '#3b82f6'
                }}>
                  What Happens Next?
                </h3>
              </div>
              
              <div style={{ 
                display: 'flex', 
                flexDirection: 'column', 
                gap: '16px',
                paddingLeft: '52px'
              }}>
                <div style={{ display: 'flex', alignItems: 'start', gap: '12px' }}>
                  <div style={{
                    minWidth: '24px',
                    height: '24px',
                    borderRadius: '50%',
                    background: 'rgba(59, 130, 246, 0.2)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '12px',
                    fontWeight: '700',
                    color: '#3b82f6',
                    marginTop: '2px'
                  }}>1</div>
                  <div>
                    <div style={{ fontWeight: '600', marginBottom: '4px', fontSize: '14px', color: 'var(--text-primary)' }}>
                      Admin Reviews Your Request
                    </div>
                    <div style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>
                      Our team will review your verification request within 24 hours
                    </div>
                  </div>
                </div>

                <div style={{ display: 'flex', alignItems: 'start', gap: '12px' }}>
                  <div style={{
                    minWidth: '24px',
                    height: '24px',
                    borderRadius: '50%',
                    background: 'rgba(59, 130, 246, 0.2)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '12px',
                    fontWeight: '700',
                    color: '#3b82f6',
                    marginTop: '2px'
                  }}>2</div>
                  <div>
                    <div style={{ fontWeight: '600', marginBottom: '4px', fontSize: '14px', color: 'var(--text-primary)' }}>
                      You'll Receive a Video Call
                    </div>
                    <div style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>
                      An admin will initiate a video call to verify your identity
                    </div>
                  </div>
                </div>

                <div style={{ display: 'flex', alignItems: 'start', gap: '12px' }}>
                  <div style={{
                    minWidth: '24px',
                    height: '24px',
                    borderRadius: '50%',
                    background: 'rgba(59, 130, 246, 0.2)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '12px',
                    fontWeight: '700',
                    color: '#3b82f6',
                    marginTop: '2px'
                  }}>3</div>
                  <div>
                    <div style={{ fontWeight: '600', marginBottom: '4px', fontSize: '14px', color: 'var(--text-primary)' }}>
                      Quick Verification (1-2 minutes)
                    </div>
                    <div style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>
                      Show a simple hand gesture to confirm you're a real person
                    </div>
                  </div>
                </div>

                <div style={{ display: 'flex', alignItems: 'start', gap: '12px' }}>
                  <div style={{
                    minWidth: '24px',
                    height: '24px',
                    borderRadius: '50%',
                    background: 'rgba(16, 185, 129, 0.2)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '12px',
                    fontWeight: '700',
                    color: '#10b981',
                    marginTop: '2px'
                  }}>✓</div>
                  <div>
                    <div style={{ fontWeight: '600', marginBottom: '4px', fontSize: '14px', color: '#10b981' }}>
                      Get Your Verification Badge!
                    </div>
                    <div style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>
                      Your profile will display the verified badge once approved
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div style={{
              background: 'rgba(251, 191, 36, 0.1)',
              border: '1px solid rgba(251, 191, 36, 0.3)',
              padding: '16px',
              borderRadius: '12px',
              marginBottom: '24px',
              display: 'flex',
              alignItems: 'start',
              gap: '12px',
              textAlign: 'left'
            }}>
              <div style={{ 
                fontSize: '13px',
                fontWeight: '700',
                marginTop: '2px',
                minWidth: '80px',
                height: '32px',
                borderRadius: '8px',
                background: 'rgba(251, 191, 36, 0.15)',
                border: '1px solid rgba(251, 191, 36, 0.3)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                color: '#f59e0b',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}>
                Important
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: '600', marginBottom: '6px', fontSize: '14px', color: '#f59e0b' }}>
                  Important Reminder
                </div>
                <div style={{ fontSize: '13px', color: 'var(--text-secondary)', lineHeight: '1.5' }}>
                  Keep your notifications enabled and stay available. Missing the call may delay your verification.
                </div>
              </div>
            </div>

            <button
              onClick={() => navigate('/profile')}
              className="btn btn-primary"
              style={{ 
                width: '100%',
                padding: '14px',
                fontSize: '15px',
                fontWeight: '700',
                background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a6f 100%)',
                border: 'none',
                borderRadius: '12px',
                cursor: 'pointer',
                transition: 'transform 0.2s ease, box-shadow 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = '0 8px 16px rgba(255, 107, 107, 0.3)';
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = 'none';
              }}
            >
              Back to Profile
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default RequestVerification;
